import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import useMutationCreateBlacklistReport from '../../api/hooks/useMutationCreateBlacklistReport'
import { BlacklistQueryKeys } from '../../keys'

const formSchema = z.object({
	accountNumber: z.string().min(1, 'กรุณาระบุเลขบัญชี'),
	accountName: z.string().min(1, 'กรุณาระบุชื่อบัญชี'),
	bankProviderName: z.string().min(1, 'กรุณาเลือกธนาคาร'),
	phoneNumber: z.string().min(1, 'กรุณาระบุเบอร์โทรศัพท์'),
	reason: z.string().min(1, 'กรุณาระบุเหตุผล'),
})

type FormValues = z.infer<typeof formSchema>

interface BlacklistFormModalProps {
	trigger?: React.ReactNode
}

export const BlacklistFormModal = ({ trigger }: BlacklistFormModalProps) => {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			accountNumber: '',
			accountName: '',
			bankProviderName: '',
			phoneNumber: '',
			reason: '',
		},
	})

	const { mutate: createBlacklistReport, isPending: isCreating } =
		useMutationCreateBlacklistReport()

	const onSubmit = (values: FormValues) => {
		try {
			createBlacklistReport(values, {
				onSuccess: () => {
					toast.success('เพิ่มรายการ Blacklist สำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [BlacklistQueryKeys.GetBlacklist],
					})
					form.reset()
					setOpen(false)
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message || 'เกิดข้อผิดพลาด')
				},
			})
		} catch {
			toast.error('เกิดข้อผิดพลาดในการส่งข้อมูล')
		}
	}

	const defaultTrigger = (
		<Button variant="default">
			<PlusIcon className="h-4 w-4" />
			เพิ่มรายการใหม่
		</Button>
	)

	return (
		<>
			<div onClick={() => setOpen(true)}>{trigger || defaultTrigger}</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
					<DialogHeader>
						<DialogTitle>เพิ่มรายการ Blacklist</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<FormField
									control={form.control}
									name="accountNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> เลขบัญชี
											</FormLabel>
											<FormControl>
												<Input placeholder="กรุณาระบุเลขบัญชี" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="accountName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> ชื่อบัญชี
											</FormLabel>
											<FormControl>
												<Input placeholder="กรุณาระบุชื่อบัญชี" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<BankSelector
									control={form.control}
									name="bankProviderName"
									label="ธนาคาร"
									required={true}
								/>

								<FormField
									control={form.control}
									name="phoneNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> เบอร์โทรศัพท์
											</FormLabel>
											<FormControl>
												<Input
													placeholder="กรุณาระบุเบอร์โทรศัพท์"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="reason"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> เหตุผล
										</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรุณาระบุเหตุผลในการเพิ่มเข้า Blacklist"
												className="min-h-[100px]"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setOpen(false)}
								>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={isCreating}>
									{isCreating ? 'กำลังเพิ่ม...' : 'เพิ่มรายการ'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
